import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { searchIc } from "../../assets";
import "../styles/search.css";

const SearchBar = ({
  onSearch,
  query = "",
  suggestions = [],
  onSuggestionSelect,
}) => {
  const { i18n, t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isActiveMobile, setIsActiveMobile] = useState(false);
  const inputRef = useRef(null);
  const mouseDownTargetRef = useRef(null);
  const panelId = useId();
  const isRtl = i18n.dir() === "rtl";
  const showSuggestions = (expanded || isActiveMobile) && query.trim() && suggestions.length > 0;

  useEffect(() => {
    if ((expanded || isActiveMobile) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded, isActiveMobile]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsActiveMobile(false);
        setExpanded(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (isActiveMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isActiveMobile]);

  const handleBlur = () => {
    if (window.innerWidth >= 768 && !query) {
      setExpanded(false);
    }
  };

  const handleFocus = () => {
    if (window.innerWidth < 768) {
      setIsActiveMobile(true);
    }
  };

  const handleClear = () => {
    onSearch("");
    setExpanded(false);
    setIsActiveMobile(false);
  };

  const handleCancel = (event) => {
    event.stopPropagation();
    setIsActiveMobile(false);
    inputRef.current?.blur();
  };

  const handleOpen = () => {
    if (expanded) {
      inputRef.current?.focus();
      return;
    }
    setExpanded(true);
  };

  const handleMouseDown = (event) => {
    mouseDownTargetRef.current = event.target;
  };

  const handleOverlayClick = (event) => {
    // If the click starts and ends directly on the overlay background element, close it
    if (
      event.target === event.currentTarget &&
      mouseDownTargetRef.current === event.currentTarget
    ) {
      setIsActiveMobile(false);
      inputRef.current?.blur();
    }
  };

  const handleSuggestionClick = (slug) => {
    onSuggestionSelect?.(slug);
    setIsActiveMobile(false);
    setExpanded(false);
  };

  return (
    <>
      <div
        id={panelId}
        className={`search-input-panel${expanded ? " expanded" : ""}${
          isActiveMobile ? " mobile-active" : ""
        }${isRtl ? " rtl" : ""}`}
        onMouseDown={handleMouseDown}
        onClick={handleOverlayClick}
      >
        <div className="search-bar-row">
          <div className="search-field-container">
            <input
              ref={inputRef}
              type="text"
              className="search-input-animated"
              placeholder={t("search.placeholder")}
              value={query}
              onChange={(event) => onSearch(event.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              aria-label={t("search.aria")}
              autoComplete="off"
            />

            <span className="search-panel-icon" aria-hidden="true">
              <img src={searchIc} alt="" style={{ width: 18, height: 18 }} />
            </span>

            {query && (
              <button
                className="search-clear-btn"
                type="button"
                onClick={handleClear}
                aria-label={t("search.clear")}
              >
                x
              </button>
            )}
          </div>

          {isActiveMobile && (
            <button
              className="search-cancel-btn"
              type="button"
              onClick={handleCancel}
            >
              {t("search.cancel") || "Cancel"}
            </button>
          )}
        </div>

        {showSuggestions && (
          <ul className={`search-suggestions${isRtl ? " rtl" : ""}`}>
            {suggestions.map((suggestion) => (
              <li key={suggestion.slug}>
                <button
                  className="search-suggestion-item"
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSuggestionClick(suggestion.slug)}
                >
                  <span className="search-suggestion-title">
                    {suggestion.title}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        className={`search-toggle-btn${isRtl ? " rtl" : ""}`}
        type="button"
        onClick={handleOpen}
        aria-label={t("search.open")}
        aria-controls={panelId}
        aria-expanded={expanded}
      >
        <img src={searchIc} alt="" style={{ width: 20, height: 20 }} />
      </button>
    </>
  );
};

export default SearchBar;

